import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, User, Phone } from "lucide-react";
import { NAV_LINKS, CONTENT, telHref } from "../data/site";

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-4 md:px-12">
          <button
            data-testid="logo-button"
            onClick={() => go("#home")}
            className="flex shrink-0 items-center text-left"
          >
            <span className="flex items-center rounded-md bg-white px-2.5 py-1.5">
              <img src="/logo.png" alt="Renuka Roofing Structurals" className="h-11 w-auto md:h-12" />
            </span>
          </button>

          <nav className="ml-10 hidden items-center gap-4 2xl:flex" data-testid="desktop-nav">
            {NAV_LINKS.slice(1).map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => go(l.href)}
                className="whitespace-nowrap text-[13px] font-medium uppercase tracking-wider text-[#A3A3A3] transition-colors hover:text-white"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="ml-6 flex items-center gap-3">
            <a
              data-testid="header-call-link"
              href={telHref()}
              className="ml-4 hidden shrink-0 items-center gap-2 whitespace-nowrap text-[13px] font-semibold tracking-wide text-white/80 transition-colors hover:text-[#3377FF] lg:flex 2xl:hidden min-[1800px]:flex"
            >
              <Phone size={14} className="text-[#3377FF]" /> {CONTENT.phone}
            </a>
            <button
              data-testid="header-quote-button"
              onClick={() => go("#contact")}
              className="hidden items-center gap-2 whitespace-nowrap bg-[#0055FF] px-5 py-2.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-all hover:bg-[#3377FF] hover:scale-[1.03] sm:flex"
            >
              Get a Free Quote <ArrowUpRight size={15} />
            </button>
            <a
              data-testid="header-login-link"
              href="/admin"
              className="hidden items-center gap-1.5 border border-white/20 px-4 py-2.5 text-[13px] font-semibold uppercase tracking-wider text-white/80 transition-colors hover:border-[#0055FF] hover:text-white md:flex"
            >
              <User size={14} /> Login
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="text-white 2xl:hidden"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#050505]"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="flex items-center rounded-md bg-white px-2 py-1">
                <img src="/logo.png" alt="Renuka Roofing Structurals" className="h-10 w-auto" />
              </span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(l.href)}
                  className="border-b border-white/10 py-4 text-left font-display text-2xl font-semibold tracking-tight text-white/90 transition-colors hover:text-[#3377FF]"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <button
                data-testid="mobile-quote-button"
                onClick={() => go("#contact")}
                className="flex w-full items-center justify-center gap-2 bg-[#0055FF] py-4 font-display text-sm font-bold uppercase tracking-widest text-white"
              >
                Get a Free Quote <ArrowUpRight size={16} />
              </button>
              <a
                data-testid="mobile-login-link"
                href="/admin"
                className="mt-3 flex w-full items-center justify-center gap-2 border border-white/20 py-4 font-display text-sm font-bold uppercase tracking-widest text-white/80 transition-colors hover:border-[#0055FF]"
              >
                <User size={16} /> Admin Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
